import { MakeModel } from '@app/_models/makeModel';
import { StateCity } from '@app/_models/stateCity';

/** Subset of the /pdf/extract-policy response that Create Package can use. All values arrive as strings. */
export interface InvoiceExtractionData {
  vehicleMake?: string;
  model?: string;
  variant?: string;
  yearOfMfg?: string;
  chassisNo?: string;
  engineNo?: string;
  vehicleRegistrationNo?: string;
  invoiceValue?: string;
  vehicleColor?: string;
  cubicCapacity?: string;
  insured?: string;
  gender?: string;
  hypothecated?: string;
  financeCompanyName?: string;
  financeCompanyAddress?: string;
  financeCompanyState?: string;
  financeCompanyCity?: string;
  dealerAddress?: string;
  dealerState?: string;
  dealerCity?: string;
  addressOfInsured?: string;
  contactNumber?: string;
  email?: string;
  state?: string;
  city?: string;
  pincode?: string;
}

/** Form controls the extractor is allowed to touch. */
export type InvoiceTargetField =
  | 'vehicleMake'
  | 'model'
  | 'variant'
  | 'vehicleYear'
  | 'chasisNumber'
  | 'engineNumber'
  | 'registrationNumber'
  | 'invoiceValue'
  | 'vehicleColor'
  | 'cubicCapacity'
  | 'gender'
  | 'hypothecated'
  | 'financeCompanyName'
  | 'financeCompanyAddress'
  | 'financeCompanyState'
  | 'financeCompanyCity'
  | 'title'
  | 'firstName'
  | 'middleName'
  | 'lastName'
  | 'contactNumber'
  | 'email'
  | 'address'
  | 'state'
  | 'city'
  | 'pincode';

export interface ProposedValue {
  field: InvoiceTargetField;
  label: string;
  value: string | number;
}

export interface MappingContext {
  makeModel: MakeModel[];
  years: number[];
  vehicleType: string; // 'New' | 'Old'
  stateCity?: StateCity[];
  /** Makes the dealer is authorised for (dealer record) that may be missing from the dropdown. */
  dealerMakes?: string[];
  financeCompanies?: string[];
  isCorporate?: boolean;
}

const norm = (s: string) => (s || '').toLowerCase().replace(/\+/g, 'plus').replace(/[^a-z0-9]/g, '');

const COMPANY_WORDS = /\b(limited|ltd|pvt|private|india|company|co|corporation|corp)\b\.?/g;
const normMake = (s: string) => norm((s || '').toLowerCase().replace(COMPANY_WORDS, ' '));

/** Case/spacing-insensitive exact match against catalogue options; returns the catalogue's own spelling. */
export function matchOption(value: string, options: string[]): string | null {
  const n = norm(value);
  if (!n || !options) return null;
  return options.find((o) => norm(o) === n) || null;
}

// Renamed cities: invoices print the new spelling, the dropdown often still has the old one.
const CITY_ALIASES: Record<string, string> = {
  bengaluru: 'bangalore', bengalururural: 'bangalorerural', bengaluruurban: 'bangaloreurban',
  mysuru: 'mysore', mangaluru: 'mangalore', belagavi: 'belgaum', kalaburagi: 'gulbarga',
  vadodara: 'baroda', gurugram: 'gurgaon', prayagraj: 'allahabad', thiruvananthapuram: 'trivandrum',
};
const normCity = (s: string) => {
  const n = norm(s);
  for (const [k, v] of Object.entries(CITY_ALIASES)) if (n.startsWith(k)) return v + n.slice(k.length);
  return n;
};

/** Exact (alias-aware) match, then a unique containment match ("Bangalore Rural" vs "Bangalore"). */
export function matchCity(value: string, options: string[]): string | null {
  const n = normCity(value);
  if (!n || !options) return null;
  const exact = options.find((o) => normCity(o) === n);
  if (exact) return exact;
  const hits = options.filter((o) => {
    const k = normCity(o);
    return k.length >= 4 && (n.startsWith(k) || k.startsWith(n));
  });
  return hits.length === 1 ? hits[0] : null;
}

/**
 * Make names on invoices are often shortened ("Hero" vs "Hero Motocorp Limited").
 * Exact match first; otherwise accept a prefix match only when exactly one catalogue make fits.
 */
export function matchMake(value: string, makes: string[]): string | null {
  const exact = matchOption(value, makes);
  if (exact) return exact;
  const n = normMake(value);
  if (n.length < 3) return null;
  const hits = (makes || []).filter((o) => {
    const k = normMake(o);
    return k === n || k.startsWith(n) || n.startsWith(k);
  });
  return hits.length === 1 ? hits[0] : null;
}

export function normalizePhone(raw: string): string | null {
  let d = (raw || '').replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('91')) d = d.slice(2);
  if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
  return /^[6-9]\d{9}$/.test(d) ? d : null;
}

export function normalizePincode(raw: string): string | null {
  const d = (raw || '').replace(/\s/g, '');
  return /^[1-9]\d{5}$/.test(d) ? d : null;
}

/** "AASIF HUSAIN" → first/last; the form has no visible middle-name input, so extra words stay in firstName. Organisations stay whole in firstName. */
export function splitName(raw: string, isCorporate: boolean): { firstName?: string; middleName?: string; lastName?: string } {
  const name = (raw || '').replace(/\s+/g, ' ').replace(/[^A-Za-z0-9 .&'-]/g, '').trim();
  if (!name) return {};
  if (isCorporate) return { firstName: name };
  const parts = name.split(' ');
  if (parts.length === 1) return { firstName: parts[0] };
  return {
    firstName: parts.slice(0, -1).join(' '),
    lastName: parts[parts.length - 1],
  };
}

export function normalizeChassis(raw: string): string | null {
  const v = (raw || '').toUpperCase().replace(/[\s-]/g, '');
  return /^[A-HJ-NPR-Z0-9]{17}$/.test(v) ? v : null;
}

export function normalizeEngine(raw: string): string | null {
  const v = (raw || '').toUpperCase().replace(/[\s-]/g, '');
  return /^[A-Z0-9]{5,20}$/.test(v) ? v : null;
}

export function normalizeRegistration(raw: string): string | null {
  const v = (raw || '').toUpperCase().replace(/[\s-]/g, '');
  return /^[A-Z]{2}\d{1,2}[A-Z]{0,3}\d{4}$/.test(v) ? v : null;
}

/** Digits (with optional commas / decimals) → positive number, otherwise null. */
export function parseAmount(raw: string, min: number, max: number): number | null {
  const v = (raw || '').replace(/[₹,\s]|rs\.?|inr/gi, '');
  if (!/^\d+(\.\d{1,2})?$/.test(v)) return null;
  const n = Math.round(Number(v));
  return n >= min && n <= max ? n : null;
}

/**
 * Turn the raw API payload into validated, form-ready proposals.
 * Anything missing, ambiguous or failing validation is simply omitted (never guessed).
 */
export function mapInvoiceData(data: InvoiceExtractionData, ctx: MappingContext): ProposedValue[] {
  const out: ProposedValue[] = [];
  if (!data) return out;

  // Only makes the dealer can actually pick are allowed. With a price list the dropdown is already that list;
  // otherwise it is narrowed to the dealer's authorised makes.
  const dealerMakes = ctx.dealerMakes || [];
  const listed = (ctx.makeModel || []).map((m) => m.make);
  const allowed = dealerMakes.length ? listed.filter((m) => dealerMakes.includes(m)) : listed;
  const matchedMake = data.vehicleMake
    ? matchMake(data.vehicleMake, allowed) || (dealerMakes.length ? matchMake(data.vehicleMake, dealerMakes) : null)
    : null;
  // The invoice may not print the manufacturer at all; when only one make is possible, that is the make.
  const onlyMake = allowed.length === 1 ? allowed[0] : dealerMakes.length === 1 ? dealerMakes[0] : null;
  const make = matchedMake || onlyMake;
  if (make) {
    out.push({ field: 'vehicleMake', label: 'Make', value: make });
  }

  // Model is shown as free text in the form, so it does not have to exist in the dropdown.
  // If it does match the make's catalogue, use the catalogue's own spelling.
  const rawModel = (data.model || '').trim();
  if (rawModel) {
    const models = (ctx.makeModel || []).find((m) => m.make === make)?.model || [];
    out.push({ field: 'model', label: 'Model', value: matchOption(rawModel, models) || rawModel });
  }

  const variant = (data.variant || '').trim();
  if (variant) out.push({ field: 'variant', label: 'Variant', value: variant });

  const year = /^\d{4}$/.test((data.yearOfMfg || '').trim()) ? Number(data.yearOfMfg.trim()) : null;
  if (year && (ctx.years || []).includes(year)) out.push({ field: 'vehicleYear', label: 'Manufacturing Year', value: year });

  const chassis = normalizeChassis(data.chassisNo);
  if (chassis) out.push({ field: 'chasisNumber', label: 'Chassis Number', value: chassis });

  const engine = normalizeEngine(data.engineNo);
  if (engine) out.push({ field: 'engineNumber', label: 'Engine Number', value: engine });

  if (ctx.vehicleType === 'Old') {
    const reg = normalizeRegistration(data.vehicleRegistrationNo);
    if (reg) out.push({ field: 'registrationNumber', label: 'Registration Number', value: reg });
  }

  const invoiceValue = parseAmount(data.invoiceValue, 1000, 100000000);
  if (invoiceValue) out.push({ field: 'invoiceValue', label: 'Invoice Value', value: invoiceValue });

  const cc = parseAmount(data.cubicCapacity, 30, 10000);
  if (cc) out.push({ field: 'cubicCapacity', label: 'Cubic Capacity', value: cc });

  const colour = (data.vehicleColor || '').trim();
  if (/^[A-Za-z][A-Za-z0-9 \/&.-]{1,39}$/.test(colour)) out.push({ field: 'vehicleColor', label: 'Vehicle Color', value: colour });

  // ---- customer details ----
  const nm = splitName(data.insured, !!ctx.isCorporate);
  if (nm.firstName) out.push({ field: 'firstName', label: ctx.isCorporate ? 'Organisation Name' : 'First Name', value: nm.firstName });
  if (nm.middleName) out.push({ field: 'middleName', label: 'Middle Name', value: nm.middleName });
  if (nm.lastName) out.push({ field: 'lastName', label: 'Last Name', value: nm.lastName });

  const g = (data.gender || '').trim().toLowerCase();
  const gender = g === 'male' || g === 'm' ? 'Male' : g === 'female' || g === 'f' ? 'Female' : null;
  if (gender) {
    out.push({ field: 'gender', label: 'Gender', value: gender });
    // Title only follows for Male (Mrs./Miss depends on marital status, which the invoice does not say).
    if (gender === 'Male' && !ctx.isCorporate) out.push({ field: 'title', label: 'Title', value: 'Mr' });
  }

  const phone = normalizePhone(data.contactNumber);
  if (phone) out.push({ field: 'contactNumber', label: 'Contact Number', value: phone });

  const email = (data.email || '').trim();
  if (/^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test(email)) out.push({ field: 'email', label: 'Email', value: email });

  const address = (data.addressOfInsured || '').replace(/\s+/g, ' ').trim();
  if (address.length >= 5) out.push({ field: 'address', label: 'Address', value: address });

  const states = ctx.stateCity || [];
  const state = data.state ? matchOption(data.state, states.map((s) => s.state)) : null;
  if (state) {
    out.push({ field: 'state', label: 'State', value: state });
    const city = data.city ? matchCity(data.city, states.find((s) => s.state === state)?.city || []) : null;
    if (city) out.push({ field: 'city', label: 'City', value: city });
  }

  const pin = normalizePincode(data.pincode);
  if (pin) out.push({ field: 'pincode', label: 'Pincode', value: pin });

  // ---- hypothecation / finance ----
  const hyp = (data.hypothecated || '').trim().toLowerCase();
  const financier = (data.financeCompanyName || '').replace(/\s+/g, ' ').trim();
  if (hyp === 'yes' && financier) {
    out.push({ field: 'hypothecated', label: 'Hypothecated Vehicle', value: 'Yes' });
    // Use the dropdown's own spelling when the financier is in the list; otherwise keep the printed name.
    out.push({ field: 'financeCompanyName', label: 'Finance Company', value: matchMake(financier, ctx.financeCompanies || []) || financier });
    // The finance agency address is the one printed for the financier; when the invoice has none,
    // the dealer (agency) address from the invoice header is used, as agreed with the business.
    const clean = (v: string) => (v || '').replace(/\s+/g, ' ').trim();
    // Invoices often print the town twice ("FARIDABAD FARIDABAD"); keep one copy of any immediately repeated word.
    const dedupe = (v: string) => v.replace(/\b([A-Za-z0-9]+)(\s+\1\b)+/gi, '$1');
    const finAddress = dedupe(clean(data.financeCompanyAddress) || clean(data.dealerAddress));
    if (finAddress.length >= 5) out.push({ field: 'financeCompanyAddress', label: 'Finance Address', value: finAddress });
    const finStateRaw = clean(data.financeCompanyState) || clean(data.dealerState);
    const finCityRaw = clean(data.financeCompanyState) ? clean(data.financeCompanyCity) : clean(data.dealerCity);
    const finState = finStateRaw ? matchOption(finStateRaw, states.map((s) => s.state)) : null;
    if (finState) {
      out.push({ field: 'financeCompanyState', label: 'Finance State', value: finState });
      const finCity = finCityRaw ? matchCity(finCityRaw, states.find((s) => s.state === finState)?.city || []) : null;
      if (finCity) out.push({ field: 'financeCompanyCity', label: 'Finance City', value: finCity });
    }
  } else if (hyp === 'no') {
    out.push({ field: 'hypothecated', label: 'Hypothecated Vehicle', value: 'No' });
  }
  return out;
}
